import React from 'react';
import '../styles/QualityAssurance.css';

const QualityAssurance = () => {
    const qualityStandards = [
        {
            title: 'ISO 9001:2015',
            description: 'Quality Management System certification ensuring consistent quality across all processes',
            icon: '🏆'
        },
        {
            title: 'API Certification',
            description: 'American Petroleum Institute standards for engine oil performance and quality',
            icon: '⚡'
        },
        {
            title: 'BIS Certification',
            description: 'Bureau of Indian Standards certification for quality and safety compliance',
            icon: '🇮🇳'
        },
        {
            title: 'OEM Approvals',
            description: 'Original Equipment Manufacturer approvals for specific vehicle applications',
            icon: '🔧'
        }
    ];

    const testingProcedures = [
        {
            title: 'Physical Properties Testing',
            items: ['Viscosity measurement', 'Flash point testing', 'Pour point determination', 'Density measurement']
        },
        {
            title: 'Chemical Analysis',
            items: ['Elemental analysis', 'Additive content', 'Acid number', 'Base number']
        },
        {
            title: 'Performance Testing',
            items: ['Engine testing', 'Oxidation stability', 'Wear protection', 'Corrosion resistance']
        },
        {
            title: 'Environmental Testing',
            items: ['Biodegradability', 'Toxicity testing', 'Emissions analysis', 'Environmental impact']
        }
    ];

    const qualityProcess = [
        {
            step: '01',
            title: 'Raw Material Selection',
            description: 'Carefully selected base oils and additives from approved suppliers'
        },
        {
            step: '02',
            title: 'Formulation Development',
            description: 'Advanced R&D to create optimal lubricant formulations'
        },
        {
            step: '03',
            title: 'Manufacturing Control',
            description: 'Automated blending with precise quality control at every stage'
        },
        {
            step: '04',
            title: 'Quality Testing',
            description: 'Comprehensive testing of every batch before approval'
        },
        {
            step: '05',
            title: 'Packaging & Storage',
            description: 'Proper packaging and storage conditions to maintain quality'
        },
        {
            step: '06',
            title: 'Distribution',
            description: 'Controlled distribution network to ensure product integrity'
        }
    ];

    return (
        <div className="quality-assurance">
            {/* Hero Section */}
            <section className="qa-hero">
                <div className="container">
                    <div className="qa-hero-content">
                        <h1>Quality Assurance</h1>
                        <p>Uncompromising commitment to quality, safety, and performance in every product we manufacture</p>
                    </div>
                </div>
            </section>

            {/* Quality Standards */}
            <section className="quality-standards">
                <div className="container">
                    <h2>Our Quality Standards</h2>
                    <div className="standards-grid">
                        {qualityStandards.map((standard, index) => (
                            <div key={index} className="standard-card">
                                <div className="standard-icon">{standard.icon}</div>
                                <h3>{standard.title}</h3>
                                <p>{standard.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quality Process */}
            <section className="quality-process">
                <div className="container">
                    <h2>Our Quality Process</h2>
                    <div className="process-timeline">
                        {qualityProcess.map((process, index) => (
                            <div key={index} className="process-step">
                                <div className="step-number">{process.step}</div>
                                <div className="step-content">
                                    <h3>{process.title}</h3>
                                    <p>{process.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testing Procedures */}
            <section className="testing-procedures">
                <div className="container">
                    <h2>Comprehensive Testing Procedures</h2>
                    <div className="testing-grid">
                        {testingProcedures.map((procedure, index) => (
                            <div key={index} className="testing-card">
                                <h3>{procedure.title}</h3>
                                <ul>
                                    {procedure.items.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lab Facilities */}
            <section className="lab-facilities">
                <div className="container">
                    <div className="lab-content">
                        <div className="lab-text">
                            <h2>State-of-the-Art Laboratory</h2>
                            <p>Our advanced testing laboratory is equipped with the latest technology and staffed by experienced technicians to ensure every product meets our rigorous quality standards.</p>
                            <div className="lab-features">
                                <div className="lab-feature">
                                    <div className="feature-icon">🔬</div>
                                    <div>
                                        <h4>Advanced Equipment</h4>
                                        <p>Latest testing equipment for accurate and reliable results</p>
                                    </div>
                                </div>
                                <div className="lab-feature">
                                    <div className="feature-icon">👨‍🔬</div>
                                    <div>
                                        <h4>Expert Technicians</h4>
                                        <p>Experienced professionals with specialized training</p>
                                    </div>
                                </div>
                                <div className="lab-feature">
                                    <div className="feature-icon">📊</div>
                                    <div>
                                        <h4>Data Analysis</h4>
                                        <p>Comprehensive data analysis and reporting</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lab-image">
                            <img src="/logo/original_logo.jpeg" alt="MapLube Laboratory" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="certifications">
                <div className="container">
                    <h2>Certifications & Approvals</h2>
                    <div className="certifications-grid">
                        <div className="certification-item">
                            <div className="cert-icon">🏆</div>
                            <h3>ISO 9001:2015</h3>
                            <p>Quality Management System certification ensuring consistent quality across all processes and continuous improvement.</p>
                        </div>
                        <div className="certification-item">
                            <div className="cert-icon">⚡</div>
                            <h3>API Certification</h3>
                            <p>American Petroleum Institute certification for engine oil performance standards and quality assurance.</p>
                        </div>
                        <div className="certification-item">
                            <div className="cert-icon">🇮🇳</div>
                            <h3>BIS Certification</h3>
                            <p>Bureau of Indian Standards certification ensuring compliance with Indian quality and safety standards.</p>
                        </div>
                        <div className="certification-item">
                            <div className="cert-icon">🔧</div>
                            <h3>OEM Approvals</h3>
                            <p>Original Equipment Manufacturer approvals for specific vehicle and equipment applications.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quality Commitment */}
            <section className="quality-commitment">
                <div className="container">
                    <div className="commitment-content">
                        <h2>Our Quality Commitment</h2>
                        <div className="commitment-grid">
                            <div className="commitment-item">
                                <div className="commitment-icon">🎯</div>
                                <h3>Zero Compromise</h3>
                                <p>We never compromise on quality, regardless of market pressures or cost considerations.</p>
                            </div>
                            <div className="commitment-item">
                                <div className="commitment-icon">🔄</div>
                                <h3>Continuous Improvement</h3>
                                <p>We continuously improve our processes and products based on feedback and new technologies.</p>
                            </div>
                            <div className="commitment-item">
                                <div className="commitment-icon">👥</div>
                                <h3>Customer Focus</h3>
                                <p>Every quality decision is made with our customers' needs and satisfaction in mind.</p>
                            </div>
                            <div className="commitment-item">
                                <div className="commitment-icon">🌱</div>
                                <h3>Environmental Responsibility</h3>
                                <p>We maintain high environmental standards while ensuring product performance and safety.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="qa-cta">
                <div className="container">
                    <div className="cta-content">
                        <h2>Experience MapLube Quality</h2>
                        <p>Join thousands of satisfied customers who trust MapLube for their lubricant needs</p>
                        <div className="cta-buttons">
                            <button className="btn btn-primary">Contact Quality Team</button>
                            <button className="btn btn-secondary">Download Quality Report</button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default QualityAssurance; 